/**
 * Authentication Context
 * Simple email/password JWT auth (no Canva OAuth).
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { intervalManager } from '../utils/intervalManager';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost'
      ? 'http://localhost:8000/api'
      : '/api'
  );

  const fetchUserData = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser({ ...data, authenticated: true });
        return true;
      }
      localStorage.removeItem('access_token');
      setUser(null);
      return false;
    } catch {
      localStorage.removeItem('access_token');
      setUser(null);
      return false;
    }
  }, [API_BASE_URL]);

  // Restore session from localStorage on load
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUserData(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUserData]);

  /** Email + password login — throws on failure */
  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg = Array.isArray(body.detail)
        ? body.detail.map(e => e.msg).join(', ')
        : body.detail || 'Login failed';
      throw new Error(msg);
    }
    const data = await res.json();
    const token = data.access_token;
    localStorage.setItem('access_token', token);
    await fetchUserData(token);
  }, [API_BASE_URL, fetchUserData]);

  /** Accept a raw JWT token (used by deep-link flows if needed) */
  const loginWithToken = useCallback((token) => {
    localStorage.setItem('access_token', token);
    fetchUserData(token);
  }, [fetchUserData]);

  const logout = useCallback(async () => {
    intervalManager.clearAll();
    const token = localStorage.getItem('access_token');
    try {
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch { /* ignore */ }
    setUser(null);
    localStorage.removeItem('access_token');
  }, [API_BASE_URL]);

  const refreshUser = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (token) return fetchUserData(token);
    return Promise.resolve(false);
  }, [fetchUserData]);

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    needsProfileCompletion: false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
